import React, { useEffect, useState, useCallback, useRef } from "react"
import { createPortal } from "react-dom"
import PubSub from "pubsub-js"
import { api } from "aldoo-ra/API"
import Spinner from "aldoo-ra/Spinner"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showSuccessToast, TOAST_POSITIONS } from "aldoo-ra/Toast"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import Tooltip from "aldoo-ra/Tooltip"
import {
  PhotoIcon,
  FilmIcon,
  MusicalNoteIcon,
  DocumentIcon,
  TrashIcon,
  LinkIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline"
import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline"
import { downloadURL, SnapMediaStage } from "../SnapViewer"

const snapAPI = api("snap")
const PER_PAGE = 24

const TYPE_ICON = {
  image: PhotoIcon,
  video: FilmIcon,
  audio: MusicalNoteIcon,
  file: DocumentIcon,
}

const typeFilterOptions = (texts) => [
  { value: "all", label: texts?.snap_files_type_all },
  { value: "image", label: texts?.snap_files_type_images },
  { value: "video", label: texts?.snap_files_type_videos },
  { value: "audio", label: texts?.snap_files_type_audio },
]

const formatSize = (bytes) => {
  if (!bytes) return ""
  const units = ["B", "KB", "MB", "GB"]
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`
}

/**
 * "Your Files" — grid of captures (thumbnails) shared from the mac app.
 * Click opens the same /v/{hash} viewer the share links use.
 */
export default function SnapFilesPanel() {
  const texts = TextBundle({ id: "snap_files_and_usage" })
  const { template } = Localization()
  const TYPE_FILTER_OPTIONS = typeFilterOptions(texts)
  const [items, setItems] = useState(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState("all")
  const [viewerIndex, setViewerIndex] = useState(null)
  const [usage, setUsage] = useState(null)
  const viewerItem = viewerIndex !== null ? items?.[viewerIndex] : null

  // Storage banner data — refreshed with the gallery so deletes update it
  const loadUsage = useCallback(() => {
    snapAPI({ usage: {} })
      .then((res) => res && !res.error && setUsage(res))
      .catch(() => {})
  }, [])

  const load = useCallback(async (targetPage, query, type) => {
    const res = await snapAPI({
      list: {
        page: targetPage,
        itemsPerPage: PER_PAGE,
        search: query || undefined,
        type: type && type !== "all" ? type : undefined,
      },
    }).catch(() => null)
    if (!res || res.error) return
    setItems(res.items)
    setTotal(res.total)
    setPage(res.page)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => load(1, search, typeFilter), search ? 300 : 0)
    return () => clearTimeout(timer)
  }, [search, typeFilter, load])

  useEffect(() => { loadUsage() }, [loadUsage])

  // Live updates from /rt/account (bridged to PubSub by useSnapRealtime).
  // Counter patches (views/downloads) apply in place; anything structural
  // (created / uploaded / deleted) refetches the current page.
  const stateRef = useRef({ page, search, typeFilter })
  stateRef.current = { page, search, typeFilter }
  useEffect(() => {
    const token = PubSub.subscribe("snap.files.changed", (_msg, { event, payload }) => {
      if (event === "snap.viewed" || event === "snap.downloaded") {
        setItems((current) =>
          current?.map((item) =>
            item.hash === payload.hash ? { ...item, ...payload } : item
          ) ?? current
        )
        return
      }
      load(stateRef.current.page, stateRef.current.search, stateRef.current.typeFilter)
      loadUsage()
    })
    return () => PubSub.unsubscribe(token)
  }, [load, loadUsage])

  const copyLink = async (item) => {
    try {
      await navigator.clipboard.writeText(item.url)
      showSuccessToast(texts?.snap_files_link_copied_toast, "", { position: TOAST_POSITIONS.BOTTOM_CENTER })
    } catch {}
  }

  const remove = (item) => {
    showPromptDialog({
      title: texts?.snap_files_delete_dialog_title,
      message: template(texts?.snap_files_delete_dialog_message, { name: item.title || item.fileName }),
      onConfirm: async () => {
        await snapAPI({ delete: { hash: item.hash } }).catch(() => null)
        load(page, search, typeFilter)
        loadUsage()
      },
    })
  }

  const pages = Math.max(1, Math.ceil(total / PER_PAGE))

  if (items === null) {
    return (
      <div className="flex items-center justify-center min-h-[30vh]">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  // Storage banner: full → red (uploads are rejected), ≥85% → amber warning
  const storageRatio = usage?.quota ? usage.bytes / usage.quota : 0
  const storageFull = storageRatio >= 1
  const storageWarning = !storageFull && storageRatio >= 0.85

  return (
    <div className="flex flex-col gap-4">
      {(storageFull || storageWarning) && (
        <div
          className={`flex flex-wrap items-center gap-3 rounded-2xl border-2 px-4 py-3 ${
            storageFull
              ? "bg-paper/80 border-error/70"
              : "bg-paper/80 border-warning/70"
          }`}
        >
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon
              className={`w-5 h-5 stroke-2 shrink-0 ${storageFull ? "text-error" : "text-progress"}`}
            />
            <div className="flex-1 min-w-[220px] text-sm text-text">
              <span className="font-semibold">
                {storageFull
                  ? texts?.snap_files_storage_full_title
                  : texts?.snap_files_storage_warning_title}
              </span>{" "}

              <span className="text-text_secondary">
                {template(texts?.snap_files_storage_usage, {
                  used: formatSize(usage.bytes),
                  quota: formatSize(usage.quota),
                })}
                {storageFull ? texts?.snap_files_storage_full_note : "."}{" "}
                {texts?.snap_files_storage_upgrade_note}
              </span>
            </div>
          </div>

          <Button
            variant="primary"
            className="shrink-0 ml-auto"
            onClick={() => (window.location.href = "/pricing")}
          >
            {texts?.snap_files_upgrade_plan_button}
          </Button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 paper_20 p-2 rounded-2xl md:rounded-full">
        <Input
          variant="snap"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={texts?.snap_files_search_placeholder}
          className="w-full max-w-sm"
        />

        <div className="flex flex-1 items-center justify-between">
          <DropDownSelect
            variant="snap"
            options={TYPE_FILTER_OPTIONS}
            value={[TYPE_FILTER_OPTIONS.find((opt) => opt.value === typeFilter)]}
            onChange={(selection) => setTypeFilter(selection[0]?.value || "all")}
            placeholder={texts?.snap_files_type_all}
            showCheckbox={false}
            showSelectAll={false}
            enableResetButton={false}
            fixedWidth="10rem"
          />

          <div className="md:text-lg text-white font-semibold bg-theme_black shrink-0 px-5 py-0.5 rounded-full ml-auto md:mr-2">
            {template(
              total === 1 ? texts?.snap_files_count_one : texts?.snap_files_count_other,
              { count: total }
            )}
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-paper border border-bordered rounded-2xl p-12 flex flex-col items-center gap-3">
          <PhotoIcon className="w-12 h-12 text-text_secondary/40" />
          <Typography
            variant="txt_dim"
            className="text-center"
          >
            {texts?.snap_files_empty_state}
          </Typography>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item, index) => {
            const Icon = TYPE_ICON[item.type] || DocumentIcon
            return (
              <div
                key={item.hash}
                className="group bg-paper border border-bordered rounded-2xl overflow-hidden flex flex-col cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setViewerIndex(index)}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-black/5 flex items-center justify-center overflow-hidden">
                  {item.thumbnail ? (
                    <img
                      src={item.thumbnail}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon className="w-10 h-10 text-text_secondary/40" />
                  )}
                </div>

                {/* Meta */}
                <div className="p-3 flex flex-col gap-1.5">
                  <div className="truncate text-sm font-medium text-text">
                    {item.title || item.fileName || item.hash}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-text_secondary">
                    <span>{formatSize(item.size)}</span>
                    <span className="inline-flex items-center gap-1">
                      <EyeIcon className="w-3.5 h-3.5" /> {item.views}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <ArrowDownTrayIcon className="w-3.5 h-3.5" /> {item.downloads}
                    </span>
                    {item.status === "pending" && (
                      <span className="text-amber-500">{texts?.snap_files_uploading}</span>
                    )}
                  </div>
                  {/* Actions */}
                  <div
                    className="flex items-center gap-1 pt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip content={texts?.snap_files_copy_link}>
                      <Button
                        variant="icon"
                        onClick={() => copyLink(item)}
                        aria-label={texts?.snap_files_copy_link}
                      >
                        <LinkIcon className="w-4 h-4" />
                      </Button>
                    </Tooltip>
                    <Tooltip content={texts?.snap_files_download}>
                      <a
                        href={downloadURL(item.hash)}
                        className="p-1.5 text-text_secondary hover:text-text"
                        aria-label={texts?.snap_files_download}
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </a>
                    </Tooltip>
                    <div className="ml-auto">
                      <Tooltip content={texts?.snap_files_delete}>
                        <Button
                          variant="icon"
                          onClick={() => remove(item)}
                          aria-label={texts?.snap_files_delete}
                          className="hover:text-error"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Lightbox overlay — same media stage as the /v/{hash} viewer */}
      {viewerItem && (
        <SnapLightbox
          item={viewerItem}
          texts={texts}
          onClose={() => setViewerIndex(null)}
          onCopyLink={() => copyLink(viewerItem)}
          onDelete={() => {
            // Close first so the confirm dialog isn't buried under the overlay.
            setViewerIndex(null)
            remove(viewerItem)
          }}
          onPrev={viewerIndex > 0 ? () => setViewerIndex(viewerIndex - 1) : null}
          onNext={
            viewerIndex < items.length - 1
              ? () => setViewerIndex(viewerIndex + 1)
              : null
          }
        />
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button 
            variant="secondary" 
            disabled={page <= 1} 
            onClick={() => load(page - 1, search, typeFilter)}
          >
            {texts?.snap_files_previous}
          </Button>
          <span className="text-sm text-text_secondary">
            {page} / {pages}
          </span>
          <Button 
            variant="secondary" 
            disabled={page >= pages} 
            onClick={() => load(page + 1, search, typeFilter)}
          >
            {texts?.snap_files_next}
          </Button>
        </div>
      )}
    </div>
  )
}

/**
 * Fullscreen dark overlay preview for a gallery item. The media fills the
 * whole screen; the action bar (copy link, open page, download, delete,
 * close) floats on top with a gradient. Esc closes; arrows (keys + side
 * buttons) step through the gallery page.
 */
function SnapLightbox({ item, texts, onClose, onCopyLink, onDelete, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose()
      if (e.key === "ArrowLeft") onPrev?.()
      if (e.key === "ArrowRight") onNext?.()
    }
    window.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [onClose, onPrev, onNext])

  // Portal to <body> — escapes any ancestor stacking context (e.g. the
  // fixed z-50 navbar) so the overlay truly covers the whole screen.
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/95">
      {/* Stage — the media owns the entire screen; clicking the empty
          backdrop (not the media) closes */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <SnapMediaStage
          item={item}
          uploading={item.status !== "active"}
          fill
        />
      </div>

      {/* Action bar — floats over the media */}
      <div className="absolute top-0 inset-x-0 z-10 flex items-center gap-2 px-4 py-3 text-white bg-gradient-to-b from-black/70 via-black/30 to-transparent">
        <div className="flex-1 min-w-0">
          <div className="truncate font-medium">{item.title || item.fileName || item.hash}</div>
          <div className="text-xs opacity-60">{formatSize(item.size)}</div>
        </div>
        <Tooltip content={texts?.snap_files_copy_link} position="bottom">
          <Button
            variant="icon"
            onClick={onCopyLink}
            aria-label={texts?.snap_files_copy_link}
            className="text-white/70 hover:text-white px-2"
          >
            <LinkIcon className="w-5 h-5" />
          </Button>
        </Tooltip>
        <Tooltip content={texts?.snap_files_open_page} position="bottom">
          <a
            href={`/v/${item.hash}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={texts?.snap_files_open_page}
            className="inline-flex items-center justify-center h-10 px-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowTopRightOnSquareIcon className="w-5 h-5" />
          </a>
        </Tooltip>
        <Tooltip content={texts?.snap_files_download} position="bottom">
          <a
            href={downloadURL(item.hash)}
            aria-label={texts?.snap_files_download}
            className="inline-flex items-center justify-center h-10 px-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
          </a>
        </Tooltip>
        <Tooltip content={texts?.snap_files_delete} position="bottom">
          <Button
            variant="icon"
            onClick={onDelete}
            aria-label={texts?.snap_files_delete}
            className="text-white/70 hover:text-error px-2"
          >
            <TrashIcon className="w-5 h-5" />
          </Button>
        </Tooltip>
        <Tooltip content={texts?.snap_files_close} position="bottom">
          <Button
            variant="icon"
            onClick={onClose}
            aria-label={texts?.snap_files_close}
            className="p-2 text-white/70 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </Button>
        </Tooltip>
      </div>

      {/* Side navigation */}
      {onPrev && (
        <div className="absolute z-10 left-4 top-1/2 -translate-y-1/2">
          <Tooltip content={texts?.snap_files_previous}>
            <button
              onClick={onPrev}
              aria-label={texts?.snap_files_previous}
              className="p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
          </Tooltip>
        </div>
      )}
      {onNext && (
        <div className="absolute z-10 right-4 top-1/2 -translate-y-1/2">
          <Tooltip content={texts?.snap_files_next}>
            <button
              onClick={onNext}
              aria-label={texts?.snap_files_next}
              className="p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </Tooltip>
        </div>
      )}
    </div>,
    document.body
  )
}

/**
 * Simple usage stats for the account Usage section.
 */
export function SnapUsagePanel() {
  const texts = TextBundle({ id: "snap_files_and_usage" })
  const { template } = Localization()
  const [usage, setUsage] = useState(null)

  const load = useCallback(() => {
    snapAPI({ usage: {} })
      .then((res) => res && !res.error && setUsage(res))
      .catch(() => {})
  }, [])

  useEffect(() => {
    load()
    // Any snap event shifts the aggregates — refetch (cheap aggregation).
    const token = PubSub.subscribe("snap.files.changed", () => load())
    return () => PubSub.unsubscribe(token)
  }, [load])

  if (!usage) {
    return (
      <div className="flex items-center justify-center min-h-[30vh]">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  const tiles = [
    { label: texts?.snap_files_usage_files_label, value: usage.files },
    {
      label: texts?.snap_files_usage_storage_label,
      value: formatSize(usage.bytes) || "0 B",
      // plan quota from feature-lock — shown as "of X" when the API sends it
      hint: usage.quota
        ? template(texts?.snap_files_usage_storage_hint, { quota: formatSize(usage.quota) })
        : null,
    },
    { label: texts?.snap_files_usage_views_label, value: usage.views },
    { label: texts?.snap_files_usage_downloads_label, value: usage.downloads },
  ]

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {tiles.map((tile) => (
        <div key={tile.label} className="paper_50 rounded-2xl p-6 flex flex-col gap-1">
          <Typography variant="body1_dim" className="font-medium mt-0 mb-2">
            {tile.label}
          </Typography>

          <Typography 
            component="output"
            variant="h4"
          >
            {tile.value}
            {tile.hint && (
              <span className="text-sm font-normal text-text_secondary ml-1.5">
                {tile.hint}
              </span>
            )}
          </Typography>
        </div>
      ))}
    </div>
  )
}
